import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Optional,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Validators } from '@angular/forms';
import {
  DialogPresets,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { type Observable } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { HTMLSanitiser } from '../sanitise-raw-html';

export interface IRawHtmlDialogData {
  html?: string;
}

export interface IRawHtmlDialogOutput {
  html: string;
}

export function createRawHtmlDialogConfig(
  data?: IRawHtmlDialogData
): MatDialogConfig<IRawHtmlDialogData> {
  return DialogPresets.flex({
    width: '80vw',
    maxWidth: '1024px',
    height: '85vh',
    restoreFocus: false,
    autoFocus: true,
    data,
  });
}

@Component({
  selector: 'pt-raw-html-dialog',
  templateUrl: './raw-html-dialog.component.html',
  styleUrls: ['./raw-html-dialog.component.scss'],
  host: { class: 'flex flex-col h-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RawHtmlDialogComponent {
  form: TypedFormGroup<IRawHtmlDialogOutput>;
  preview$: Observable<string | undefined>;
  isEdit: boolean;

  constructor(
    private _dialogRef: MatDialogRef<
      RawHtmlDialogComponent,
      IRawHtmlDialogOutput
    >,
    @Optional() @Inject(MAT_DIALOG_DATA) data?: IRawHtmlDialogData
  ) {
    const initialHtml = data?.html ?? '';
    this.isEdit = !!initialHtml;
    this.form = new TypedFormGroup<IRawHtmlDialogOutput>({
      html: new TypedFormControl<string>(initialHtml, [
        Validators.required,
        Validators.pattern(/\S/),
      ]),
    });
    this.preview$ = this.form.controls.html.valueChanges.pipe(
      debounceTime(250),
      startWith(initialHtml)
    );
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }
    const { html } = this.form.getRawValue();
    this._dialogRef.close({ html: HTMLSanitiser.stripActiveContent(html) });
  }
}
